import { paginate } from '@/lib/utils';
import { Column } from '@/semantic-ui/components';
import { useStores } from '@/store';
import { observer } from 'mobx-react';
import AppContext from '@/AppContext';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Checkbox, Form, Grid, Label, Pagination } from 'semantic-ui-react';
import { EmployeeStatus, Moment, Nullable, moment } from 'sol-data';
import { EmployeeStatusColor } from '../DepartmentEmployeesList/utils';
import { EntityList } from '../EntityList';
import FilterDropdown from '../FilterDropdown';
import { Item, ListColumn } from '../ListLayout';
import style from './index.less';

const RESULTS_PER_PAGE = 10;

interface EducationDocumentItem extends Item {
  id: number;
  title: string;
  postedBy: string;
  postedOn: string;
  signedAt: Nullable<Moment>;
  status: EmployeeStatus;
  deleteDatetime?: Moment;
}

interface ListProps {
  employeeId: string;
  showArchived: boolean;
}

// Inside the employee profile tabs the list's fetch effect doesn't re-run when its deps
// change, so remount the list whenever the employee or "Show archived" changes.
export const EmployeeEducationDocuments = () => {
  const { employeeId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAdmin } = useContext(AppContext);
  const showArchived = isAdmin && searchParams.get('archived') === 'true';

  return (
    <EmployeeEducationDocumentsList
      key={`${employeeId}-${showArchived}`}
      employeeId={employeeId!}
      showArchived={showArchived}
    />
  );
};

const EmployeeEducationDocumentsList = observer(({ employeeId, showArchived }: ListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const { educationDocumentStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin } = useContext(AppContext);
  const status = searchParams.get('status');

  const paramToStatusEnum = () => {
    if (status === 'Complete') return EmployeeStatus.COMPLETE;
    if (status === 'Incomplete') return EmployeeStatus.INCOMPLETE;
    return undefined;
  };

  const paramToNum = () => {
    if (status === 'Complete') return 1;
    if (status === 'Incomplete') return 2;
    return undefined;
  };

  const updateFilters = () => {
    const newStatus = paramToStatusEnum();
    if (newStatus) {
      educationDocumentStore.setFilters({ status: newStatus });
    } else {
      educationDocumentStore.clearFilters();
    }
  };

  const fetchData = async () => {
    await educationDocumentStore.fetchAllForEmployee(employeeId, showArchived);
    updateFilters();
  };

  useEffect(() => {
    fetchData();
    return () => {
      educationDocumentStore.clearFilters();
      educationDocumentStore.clear();
    };
  }, [employeeId, showArchived]);

  const educationDocuments = useMemo(() => {
    return educationDocumentStore.filtered
      .map<EducationDocumentItem>(
        ({ id, title, postedBy, postedOn, educationalDocumentEmployees, deleteDatetime }) => ({
          id,
          title,
          deleteDatetime: deleteDatetime ?? undefined,
          postedBy,
          postedOn,
          signedAt: educationalDocumentEmployees?.length
            ? educationalDocumentEmployees[0].signedAt
            : null,
          status:
            educationalDocumentEmployees?.length && educationalDocumentEmployees[0]?.isSigned
              ? EmployeeStatus.COMPLETE
              : EmployeeStatus.INCOMPLETE,
        }),
      )
      .sort((a, b) => {
        if (a.status !== b.status) {
          return a.status === EmployeeStatus.INCOMPLETE ? -1 : 1;
        }
        return moment(a.postedOn).valueOf() - moment(b.postedOn).valueOf();
      });
  }, [educationDocumentStore.filtered]);

  const columns: ListColumn[] = [
    {
      name: 'Title',
      key: 'title',
      width: { sm: 7, lg: 7 },
      render: (item) => (
        <span>
          {item.title}
          {item.deleteDatetime && (
            <Label size="mini" basic className={style.archived_label}>
              Archived
            </Label>
          )}
        </span>
      ),
    },
    {
      name: 'Created By',
      key: 'postedBy',
      textAlign: 'center',
      width: { sm: 2, lg: 2 },
      render: ({ postedBy }) => <div style={{ textAlign: 'center' }}>{postedBy}</div>,
    },
    {
      name: 'Posted On',
      key: 'postedOn',
      textAlign: 'center',
      width: { sm: 2, lg: 2 },
      render: ({ postedOn }) => (
        <div style={{ textAlign: 'center' }}>{moment(postedOn).format('L')}</div>
      ),
    },
    {
      name: 'Signed At',
      key: 'signedAt',
      textAlign: 'center',
      width: { sm: 3, lg: 3 },
      render: ({ signedAt }) => (
        <div style={{ textAlign: 'center' }}>
          {signedAt ? signedAt.format('MM/DD/YYYY - h:mm:ss a') : null}
        </div>
      ),
    },
    {
      name: 'Status',
      key: 'status',
      textAlign: 'center',
      width: { sm: 2, lg: 2 },
      render: ({ status }) => (
        <div
          style={{
            textAlign: 'center',
            fontWeight: 700,
            color:
              status === EmployeeStatus.COMPLETE
                ? EmployeeStatusColor.Complete.color
                : EmployeeStatusColor.Incomplete.color,
          }}
        >
          {status}
        </div>
      ),
    },
  ];

  const handleStatusChange = (value?: number) => {
    const employeeStatus = numToValue(value);
    educationDocumentStore.setFilters({
      status: employeeStatus,
    });

    if (employeeStatus) {
      searchParams.set('status', employeeStatus.toString());
    } else {
      searchParams.delete('status');
    }

    setSearchParams(searchParams);
  };

  const handleShowArchivedChange = (checked: boolean) => {
    if (checked) {
      searchParams.set('archived', 'true');
    } else {
      searchParams.delete('archived');
    }

    setSearchParams(searchParams);
  };

  const numToValue = (value?: number) => {
    if (value === 1) return EmployeeStatus.COMPLETE;
    if (value === 2) return EmployeeStatus.INCOMPLETE;
    return undefined;
  };

  return (
    <>
      <EntityList<EducationDocumentItem>
        title="Educational Documents"
        searchLabel="Search by document or file name"
        columns={columns}
        items={paginate(educationDocuments, RESULTS_PER_PAGE, currentPage)}
        titleProp="title"
        onSearchChange={(filter) => educationDocumentStore.setFilter(filter)}
        onDismiss={() => educationDocumentStore.clearError()}
        onRefresh={fetchData}
        errorMessage={educationDocumentStore.errorMessage}
        isLoading={educationDocumentStore.isLoading}
        noNewButton
        searchHeaderContent={
          <>
            <Column>
              <div className={style.header_controls}>
                {isAdmin && (
                  <Checkbox
                    toggle
                    label="Show archived"
                    className={style.show_archived}
                    checked={showArchived}
                    onChange={(_, { checked }) => handleShowArchivedChange(!!checked)}
                  />
                )}
                <Form.Field className={style.filters}>
                  <FilterDropdown
                    name={'Filters'}
                    placeholder={'Filters'}
                    clearable
                    value={paramToNum()}
                    changeDropdown={(_, value) => handleStatusChange(value)}
                    options={[
                      {
                        text: 'Completed',
                        value: 1,
                      },
                      {
                        text: 'Incomplete',
                        value: 2,
                      },
                    ]}
                  />
                </Form.Field>
              </div>
            </Column>
          </>
        }
      />
      <Grid centered>
        <Grid.Row>
          <Pagination
            activePage={currentPage}
            totalPages={Math.ceil(educationDocuments.length / RESULTS_PER_PAGE)}
            onPageChange={(_, { activePage }) => setCurrentPage(activePage as number)}
            siblingRange={2}
            boundaryRange={0}
            firstItem={{ content: 'First' }}
            lastItem={{ content: 'Last' }}
            ellipsisItem={null}
            prevItem={null}
            nextItem={null}
          />
        </Grid.Row>
      </Grid>
    </>
  );
});
